import { FC, useContext, useEffect, useMemo } from "react";
import {
  Button,
  CircularProgress,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import { CustomFormField } from "../../../utilis/component/CustomFormElement";
import { useForm } from "react-hook-form";
import { DiagnosisContext } from ".";
import { useMutation } from "react-query";
import {
  getDiagnosisList,
  getIcdCodes,
  saveOrUpdateDignosis,
} from "../../services";
import { DiagnosisListType, defaultValues, preparePayload } from "./types";
import { useParams } from "react-router-dom";
import debounce from "../../../utilis/methods/networkHandling";
import usePopup from "../../../hooks/usePopup";
import { HL7ContexTriggerContex } from "../../../context/HL7MessageTrigger/HL7MessageTriggerProvider";
import { EmrContext } from "../../";
import CustomDateField from "../CustomDateField";

const AddDiagnosis: FC<{ close?: () => void }> = ({ close }) => {
  const theme = useTheme();
  const { setTrigger } = useContext(HL7ContexTriggerContex);
  const { encounterId } = useParams();
  const { showSnackbar } = usePopup();
  const { control, handleSubmit, setValue, reset } = useForm({
    defaultValues,
  });
  const { list, edit, setList, setEdit } = useContext(DiagnosisContext);
  const { page, patientData, triggerConsultationStart } = useContext(EmrContext);
  const {
    mutate: fetchIcdCodes,
    data: icdCodes,
    isLoading: icdCodeLoading,
  } = useMutation(getIcdCodes);

  const { mutate: fetchDiagnosis } = useMutation(getDiagnosisList, {
    onSuccess: (result) => {
      if (result.statusCode == "200") {
        setList(result.response);
      } else {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "warning",
        });
      }
    },
  });

  const { mutate: saveDiagnosis, isLoading: saveLoading } = useMutation(
    saveOrUpdateDignosis,
    {
      onSuccess: (result) => {
        setEdit(null);
        if (result.responseIndicator == "success") {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
          });
          reset({
            ...defaultValues,
            diagnosisType: "Secondary",
          });
          setTrigger({
            encounterId: encounterId as string,
            tab: "diagnosis",
          });
          triggerConsultationStart();
          fetchDiagnosis(encounterId as string);
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "error",
          });
        }
      },
    },
  );

  const handleClose = () => {
    close && close();
    if (edit) {
      setEdit(null);
    }
  };

  const onicdChange = (event: {
    target: { value: any; name: string };
    type: string;
  }) => {
    const {
      target: { value },
    } = event;
    setValue("diagnosisDetails", value.icdName);
  };

  const onSubmit = (value: any) => {
    if (Boolean(edit)) {
      const param = preparePayload({
        ...edit,
        ...value,
        isActive: true,
        encounterId: parseInt(encounterId as string),
        fromVerification: Boolean(page == "verification"),
      } as DiagnosisListType);
      saveDiagnosis(param);
    } else {
      const param = preparePayload({
        ...value,
        status: true,
        isActive: true,
        encounterId,
        diagOrder: list.length,
        fromVerification: Boolean(page == "verification"),
      });
      saveDiagnosis(param);
    }
  };

  const isSecondary = useMemo(() => {
    const isPrimaryInclude = list.find(
      (item) => item.diagnosisType == "Principal",
    );

    return Boolean(isPrimaryInclude);
  }, [list]);

  const debouncedFetchData = debounce(fetchIcdCodes, 500);

  useEffect(() => {
    fetchIcdCodes("");
  }, []);

  useEffect(() => {
    if (Boolean(edit)) {
      console.log(edit);
      reset({
        ...edit,
        diagnosisDetails: edit?.icdCode?.icdDescription,
      });
    } else {
      !isSecondary
        ? reset({
            diagnosisType: "Principal",
          })
        : reset({
            diagnosisType: "Secondary",
          });
    }
  }, [edit, isSecondary]);

  return (
    <div className="border-2 rounded">
      <div className="border-b  rounded-t p-2" style={{}}>
        <Typography color={theme.palette.primary.main} fontWeight={600}>
          {Boolean(edit) ? "Update" : "Add"} Diagnosis
        </Typography>
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-3 grid-cols-12 p-2"
      >
        <div className="col-span-full">
          <CustomFormField
            name="icdCode"
            control={control}
            element="autocomplete"
            fieldProps={{
              label: "ICD Code",
              onChange: (e) => {
                debouncedFetchData(e.target.value);
              },
            }}
            rules={{ required: "ICD Code reuired", onChange: onicdChange }}
            options={icdCodes?.response || []}
            loading={icdCodeLoading}
            autocompleteProps={{
              filterOptions: (options) => options,
              isOptionEqualToValue: (option, value) =>
                option.icdId == value.icdId,
              getOptionLabel: (option) => ` ${option.icdCode}`,
              renderOption: (props, option) => (
                <ListItem {...props}>
                  <ListItemText
                    primary={`${option.icdCode} || ${option.icdName}`}
                  />
                </ListItem>
              ),
            }}
          />
        </div>
        <div className="col-span-4">
          <CustomFormField
            name="diagnosisType"
            control={control}
            element="radio"
            fieldProps={{ label: "Diagnosis Type" }}
            radios={
              isSecondary
                ? edit?.diagnosisType == "Principal"
                  ? withPrimary
                  : withOutPrimary
                : withPrimary
            }
            rules={{ required: "Diagnosis Type required" }}
            radioProps={{ row: true }}
          />
        </div>

        <div className="col-span-6">
          <CustomFormField
            name="diagnosisDetails"
            control={control}
            element="input"
            fieldProps={{
              label: "Diagnosis Details",
              multiline: true,
              rows: 2,
            }}
            rules={{ required: false }}
          />
        </div>

        <div className="col-span-full flex justify-end items-center gap-3">
          <div className={Boolean(page == "verification") ? "" : "hidden"}>
            <CustomDateField
              name="createdDate"
              start={patientData?.encounter?.startTime}
              end={patientData?.encounter?.endTime}
              control={control}
            />
          </div>
          <div className="flex justify-end items-center gap-2">
            <Button
              disabled={saveLoading}
              endIcon={
                saveLoading && (
                  <CircularProgress
                    size={18}
                    sx={{ color: theme.palette.background.paper }}
                  />
                )
              }
              color="success"
              type="submit"
            >
              save
            </Button>
            {close && (
              <Button color="error" onClick={handleClose}>
                cancel
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddDiagnosis;

const withPrimary = [
  {
    label: "Principal",
    value: "Principal",
  },
  {
    label: "Secondary",
    value: "Secondary",
  },
  {
    label: "Reason for visit",
    value: "ReasonForVisit",
  },
];
const withOutPrimary = [
  {
    label: "Secondary",
    value: "Secondary",
  },
  {
    label: "Reason for visit",
    value: "ReasonForVisit",
  },
];
